/**
 * useAssistantChannels — derives the channels available in the widget for
 * the current bot, manages the active channel selection, and (optionally)
 * persists the user's last choice in localStorage (per public key, opt-in).
 *
 * Plan reference: PR 4 — widget shell + chooser.
 *
 * HARD RULES (Codex-locked):
 *   1. Filter to WIDGET_CHANNELS only ('voice' | 'chat'). WhatsApp must
 *      never appear here even if it somehow leaks into enabledChannels.
 *   2. Voice is the absolute fallback. If filtered list is empty, force
 *      ['voice'] so the widget always has a working channel.
 *   3. Skip the chooser when availableChannels.length <= 1.
 *   4. Persist the last-used channel ONLY when rememberLastUsedChannel === true
 *      AND a publicKey is available (avoid global cross-bot bleed).
 *      Default is OFF — every widget open shows the chooser again.
 *      Persistence is PAGE-LIFETIME ONLY: any remembered value from a
 *      previous browser session is cleared on the first read after page
 *      load, so a browser refresh always returns to the chooser (when
 *      chat is enabled). Within a single page load, mode switches are
 *      still remembered (no re-prompt on chat→voice→chat round-trip).
 *   5. Initial state must NOT read localStorage on every render — use
 *      lazy initializers (Codex B2).
 *
 * NOTE — voice-only fast path: AssistantShell uses `isVoiceOnly()` from
 * `utils/channel-resolution.ts` to bypass this hook entirely for the
 * common voice-only case, so production behavior is unchanged. This hook
 * only mounts for multi-channel bots.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { WidgetChannel, ChannelSettings } from '../types';
import {
  isWidgetChannel,
  resolveValidPick,
  sanitizeAvailableChannels,
  shouldShowChooser as computeShouldShowChooser,
} from '../utils/channel-resolution';

const STORAGE_PREFIX = 'iland-channel:';

// Page-load reset: clear any remembered channel selections from a
// previous browser session/tab so a fresh page load always returns the
// user to the chooser (when chat is enabled and no admin defaultChannel
// is set). The `rememberLastUsedChannel` feature is therefore scoped to
// the current page lifetime only — switching modes inside a single tab
// is remembered until refresh.
//
// Module-scoped flag → reset automatically on full page reload (module
// re-evaluates), preserved across in-page React remounts.
let pageLoadCleanupDone = false;
function clearRememberedOnFreshPageLoad(): void {
  if (pageLoadCleanupDone) return;
  pageLoadCleanupDone = true;
  if (typeof window === 'undefined') return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) toRemove.push(key);
    }
    for (const k of toRemove) window.localStorage.removeItem(k);
  } catch {
    // Ignore quota / privacy errors silently.
  }
}

function storageKey(publicKey: string | undefined): string | null {
  if (!publicKey) return null;
  return `${STORAGE_PREFIX}${publicKey}`;
}

/**
 * Public helper — clear the remembered channel for a specific bot.
 *
 * Used when the widget is fully closed (close button on the panel) so
 * the next open behaves like a fresh page load: the chooser is shown,
 * no cross-channel memory survives, no auto-resume. This is the
 * "Option 1 / full reset" semantics requested by the user.
 *
 * Module-scoped page-load flag is NOT touched (only relevant to first
 * read after page reload). This function purely scrubs the persisted
 * pick for a specific public key.
 */
export function clearRememberedChannel(publicKey: string | undefined): void {
  const key = storageKey(publicKey);
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore quota / privacy errors silently.
  }
}

function readRemembered(publicKey: string | undefined): WidgetChannel | null {
  // Clear once-per-page-load BEFORE the first read so the lazy initial
  // state never sees stale cross-refresh memory.
  clearRememberedOnFreshPageLoad();
  const key = storageKey(publicKey);
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    return isWidgetChannel(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeRemembered(publicKey: string | undefined, channel: WidgetChannel): void {
  const key = storageKey(publicKey);
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, channel);
  } catch {
    // Ignore quota / privacy errors silently.
  }
}

export interface UseAssistantChannelsOptions {
  enabledChannels?: WidgetChannel[] | string[] | null;
  channelSettings?: ChannelSettings;
  publicKey?: string;
}

export interface UseAssistantChannelsResult {
  /** Filtered, deduped list of channels supported in the widget. Never empty. */
  availableChannels: WidgetChannel[];
  /** Currently selected channel (always one of availableChannels). */
  activeChannel: WidgetChannel;
  /** Whether the chooser should be shown — true only if user has more than one choice AND no valid pick yet. */
  showChooser: boolean;
  /** User picks a channel from the chooser. */
  selectChannel: (channel: WidgetChannel) => void;
  /** Programmatic in-session switch (used by switch-mode actions in PR 7). */
  setActiveChannel: (channel: WidgetChannel) => void;
  /** Re-open chooser (e.g. user clicks the back-arrow). No-op when only one channel. */
  reopenChooser: () => void;
}

export function useAssistantChannels(opts: UseAssistantChannelsOptions): UseAssistantChannelsResult {
  const { enabledChannels, channelSettings, publicKey } = opts;

  const availableChannels = useMemo<WidgetChannel[]>(
    () => sanitizeAvailableChannels(enabledChannels),
    [enabledChannels]
  );

  const widgetSettings = channelSettings?.widget;
  // Opt-IN: chooser appears every time unless the bot explicitly opts into
  // remembering the user's last pick. Matches the product expectation that
  // a fresh widget open always offers chat-vs-voice.
  const rememberEnabled = widgetSettings?.rememberLastUsedChannel === true;
  const defaultChannel = isWidgetChannel(widgetSettings?.defaultChannel)
    ? (widgetSettings!.defaultChannel as WidgetChannel)
    : undefined;

  // Lazy initial state — runs ONCE per mount, no per-render storage reads.
  const [activeChannel, setActiveChannelState] = useState<WidgetChannel>(() => {
    const remembered = readRemembered(publicKey);
    const pick = resolveValidPick({
      remembered,
      rememberEnabled,
      defaultChannel,
      availableChannels,
    });
    if (pick) return pick;
    return availableChannels[0];
  });

  // Chooser visibility — also lazy. (Codex round 3: defaultChannel must also
  // suppress the chooser, not just a remembered value.)
  const [showChooser, setShowChooser] = useState<boolean>(() =>
    computeShouldShowChooser({
      availableChannels,
      remembered: readRemembered(publicKey),
      rememberEnabled,
      defaultChannel,
    })
  );

  // Keep activeChannel valid if availableChannels changes (e.g. admin toggles chat off mid-session).
  const lastAvailableRef = useRef<WidgetChannel[]>(availableChannels);
  useEffect(() => {
    const previous = lastAvailableRef.current;
    lastAvailableRef.current = availableChannels;
    if (!availableChannels.includes(activeChannel)) {
      setActiveChannelState(availableChannels[0]);
    }
    if (availableChannels.length <= 1) {
      setShowChooser(false);
    } else if (previous.length <= 1) {
      // Channels grew from 1 to many — ask the user only if there is no
      // valid remembered OR default pick (Codex round 3). If a pick
      // exists, also adopt it as activeChannel so behavior is identical
      // across all growth paths (Codex round-4 nit).
      const pick = resolveValidPick({
        remembered: readRemembered(publicKey),
        rememberEnabled,
        defaultChannel,
        availableChannels,
      });
      if (pick) {
        setActiveChannelState(pick);
        setShowChooser(false);
      } else {
        setShowChooser(true);
      }
    }
  }, [availableChannels, activeChannel, publicKey, rememberEnabled, defaultChannel]);

  const selectChannel = useCallback(
    (channel: WidgetChannel) => {
      if (!availableChannels.includes(channel)) return;
      setActiveChannelState(channel);
      setShowChooser(false);
      if (rememberEnabled) writeRemembered(publicKey, channel);
    },
    [availableChannels, publicKey, rememberEnabled]
  );

  const setActiveChannel = useCallback(
    (channel: WidgetChannel) => {
      if (!availableChannels.includes(channel)) return;
      setActiveChannelState(channel);
      if (rememberEnabled) writeRemembered(publicKey, channel);
    },
    [availableChannels, publicKey, rememberEnabled]
  );

  const reopenChooser = useCallback(() => {
    if (availableChannels.length > 1) setShowChooser(true);
  }, [availableChannels]);

  // SYNCHRONOUS safety net (Codex round-2 B1):
  // Even though the useEffect above corrects state when availableChannels
  // mutates, the corrective render happens AFTER the offending render.
  // Derive a synchronously-safe channel for the current render so we
  // never report an activeChannel that isn't in availableChannels.
  const safeActiveChannel: WidgetChannel = availableChannels.includes(activeChannel)
    ? activeChannel
    : availableChannels[0];

  // Also force chooser off synchronously if we are now down to a single channel.
  const safeShowChooser = availableChannels.length > 1 ? showChooser : false;

  return {
    availableChannels,
    activeChannel: safeActiveChannel,
    showChooser: safeShowChooser,
    selectChannel,
    setActiveChannel,
    reopenChooser,
  };
}
